// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Dating a piece of jewelry is a process of elimination, not a single flash of recognition. You read four things in order: the metal, the stones, the findings, and the motifs. Each one narrows the window, and together they usually land you inside a couple of decades.

## Start with the findings

The mechanical parts are the most honest witnesses on a piece, because they changed on a known timeline and nobody bothered to fake them.

- **C clasp on a brooch**: a simple open hook with a long pin sticking out past the body of the brooch. Standard through the 1800s, disappearing after about 1900.
- **Rotating safety catch**: patented in the early 1900s and common from around 1910 onward. A brooch with one is generally twentieth century, unless a later repairer fitted it.
- **Trombone clasp**: the push and pull tube catch, mostly continental European, seen from the late 1800s into the mid 1900s.
- **Screw back earrings**: patented in the 1890s and dominant until the 1950s. If the earring screws onto the earlobe, it predates the pierced ear revival.
- **Clip back earrings**: from the 1930s onward.
- **Pierced posts with butterfly backs**: overwhelmingly 1960s and later, unless the piece has old fashioned thin wire hooks, which are much older.
- **Tube hinges and shepherd's hook wires**: earlier work, generally nineteenth century.

If the findings and the style disagree, the piece has probably been repaired or converted. That is extremely common, and it is not dishonest, it just needs to be stated in a listing.

## Then read the metal

- **High karat yellow gold, 18k and above, often with a rosy tone**: earlier work, Georgian through mid Victorian.
- **9k gold**: legalised in Britain in 1854. A 9k British piece cannot be older than that.
- **Silver topped gold**: a silver front to hold diamonds, with a gold back so the silver did not blacken the skin. Georgian and Victorian.
- **Platinum**: usable in jewelry from around 1900, dominant from Edwardian through Art Deco. It was restricted for war use in the 1940s, which is why Retro pieces are gold.
- **White gold**: developed as a platinum substitute, common from the 1920s onward.
- **Rose gold in quantity**: Retro, roughly 1935 to 1950, and again in the 2010s.

## Then look at the stones

Stone cutting has its own timeline, and it is very hard to fake convincingly.

- **Rose cut**: a flat back with a domed, faceted top and no pavilion. Georgian and Victorian, revived later as a style.
- **Old mine cut**: hand cut cushion outline with an open culet. Roughly 1700s to 1890s.
- **Old European cut**: round outline, small table, open culet. Roughly 1890 to 1930.
- **Transitional cut**: larger table, lower crown, often a small culet. Roughly 1930 to 1950.
- **Modern round brilliant**: sharp point, no culet, wide table. From the 1930s onward, universal after the 1950s.
- **Calibre cut coloured stones**: small stones cut to fit exactly into a channel with no gaps. Art Deco signature.

Our [guide to old mine and old European cuts](/blog/old-mine-cut-vs-old-european-cut-diamonds) covers how to tell those two apart in your hand.

## The eras, in order

### Georgian, roughly 1714 to 1837

Handmade, scarce and often unmarked. The tell is the setting: stones sit in **closed backs**, sealed shut, frequently with coloured metal foil behind them to boost the colour under candlelight. Expect rose cuts and early cushion cuts, high karat gold, silver fronts on diamond pieces, and motifs of ribbons, bows, urns and hearts. Georgian mourning pieces with woven hair under glass belong here too.

Practical warning: never put a foiled, closed back piece in water or an ultrasonic cleaner. Moisture behind the stone destroys the foil and the colour goes with it.

### Victorian, 1837 to 1901

Sixty four years, so the trade splits it into three moods.

- **Romantic, to about 1860**: sentimental. Snakes as symbols of eternal love, hearts, hands, lockets, seed pearls, turquoise, coloured gold.
- **Grand, roughly 1860 to 1885**: heavier and darker, shaped by the long mourning period after Prince Albert's death. Jet, onyx, black enamel, bold archaeological revival work in gold.
- **Aesthetic, roughly 1885 to 1901**: lighter again. Smaller, delicate pieces, star and crescent motifs, diamond set bar brooches.

Cut steel, pinchbeck, gold filled work and mass production all appear in this period. Not everything Victorian is precious.

### Art Nouveau, roughly 1890 to 1910

A short, distinctive rebellion. Flowing asymmetric lines, dragonflies, irises, orchids, and the female face with loose hair. The materials are often modest and the workmanship is not: horn, glass, moonstone and above all **plique a jour enamel**, which is enamel with no backing so light passes through it like a stained glass window. Value here is about design and maker rather than carats.

### Edwardian, roughly 1901 to 1915

The white era. Platinum made it possible to hold diamonds in settings so fine they almost vanish. Look for **milgrain**, the tiny beaded edge along the metal, lace like pierced filigree, garland and bow motifs, and a lot of diamonds with pearls. Everything is light, pale and airy. If a piece looks like frost, it is probably Edwardian.

### Art Deco, roughly 1920 to 1935

Geometry replaces the garland. Straight lines, stepped shapes, strong symmetry, and hard contrast between white metal and dark coloured stones: onyx, sapphire, emerald, coral, jade. Calibre cut stones fill channels exactly. Platinum first, then white gold. This is the easiest era to recognise and the most heavily reproduced, so check the findings and the stone cuts before you accept a Deco attribution.

### Retro, roughly 1935 to 1950

Big, warm and sculptural. Platinum went to the war effort, so Retro is rose and yellow gold, often in large volumes with relatively small stones. Tank track bracelets, scrolls, ribbons and bows rendered in thick gold, synthetic rubies and citrines used for scale and colour. It looks like Hollywood, because that is largely where the style came from.

### Mid century and modernist, 1950s to 1970s

Two directions at once. The commercial mainstream returns to fine, sparkly, textured gold with modern brilliant cuts. Alongside it, studio makers work in silver with abstract, sculptural forms and almost no stones. Scandinavian modernist silver from this period is a collecting field in its own right.

## Antique or vintage?

The trade uses these terms fairly consistently.

- **Antique**: at least 100 years old. In 2026, that means made before roughly 1926.
- **Vintage**: roughly 20 to 100 years old.
- **Retro**: the 1935 to 1950 style period, whatever the exact age.
- **Estate**: previously owned, at any age. It says nothing about how old the piece is.

## Where dating goes wrong

- **Converted pieces.** Victorian brooches became pendants, buttons became earrings, rings were resized and lost their marks. Read every part of a piece separately.
- **Revival styles.** The Victorians revived Ancient Greek work, the 1970s revived Art Deco, and every decade since has revived something. Style alone is never proof.
- **Replaced stones.** A modern brilliant sitting in a Victorian mount tells you about a repair, not about the mount.
- **Reproductions with old fashioned marks.** Findings and stone cuts are much harder to fake than a stamp.

When you are describing a piece for sale, say what you know and say what you are inferring. "Old European cut diamond in a platinum milgrain mount, consistent with Edwardian, unmarked" is a better and more sellable sentence than a confident date you cannot support. Our [guide to selling antique jewelry online](/blog/how-to-sell-antique-jewelry-online) goes through the rest of the listing, and you can [see how other sellers describe their pieces](/s) on Nextmine.

If you would rather be walked through it, the Hallmark Society's [date your piece](https://hallmarksociety.com/tools/date-your-piece/) tool asks about findings, metal and construction, and narrows the period as you answer.
`;

export default body;
